/**
 * 媒體庫上傳的前置處理：檔案驗證、資料夾展開、路徑轉標籤。
 *
 * 抽出成獨立檔案而非留在頁面裡，是因為這些是純函式且與 React 無關；
 * MediaLibrary.tsx 已接近 500 行，把佇列邏輯也塞進去會難以閱讀。
 */

import type { MediaType } from './api-client';

/**
 * 單檔上限，**鏡射**伺服器端 `cms-media.service.ts` 的 SIZE_LIMITS 與
 * `cms-media.controller.ts` 的 MAX_UPLOAD_BYTES。
 *
 * 前端擋只是為了讓使用者當下就知道原因，不是安全邊界——真正的約束在
 * DDL 的 `chk_media_size_per_type` 與 multer 的 fileSize。兩邊數值若不一致，
 * 後端仍會擋下，只是錯誤訊息比較難懂。
 *
 * document 在後端沒有型別上限，但 multipart 整體上限 200 MB 仍然適用。
 */
export const CLIENT_SIZE_LIMITS: Record<MediaType, number> = {
  image: 20 * 1024 * 1024,
  video: 200 * 1024 * 1024,
  audio: 50 * 1024 * 1024,
  document: 200 * 1024 * 1024,
};

/** 單次可加入佇列的檔案數上限；拖進整棵大目錄時避免瀏覽器卡死。 */
export const MAX_BATCH_FILES = 500;

/** 與伺服器端 `inferMediaType` 同一套規則（依 MIME 前綴）。 */
export function inferMediaType(mimeType: string): MediaType {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * 檔案是否可上傳；不可上傳時回傳給使用者看的原因，可上傳回 null。
 *
 * 空檔案要單獨擋：後端雖有 MEDIA_EMPTY_FILE，但拖資料夾時很容易夾帶
 * `.DS_Store`、`Thumbs.db` 這類 0 byte 或系統檔，讓它們一路送到後端只是
 * 白費往返。
 */
export function validateFile(file: File): string | null {
  if (file.size === 0) return '檔案為空（0 byte）';
  const type = inferMediaType(file.type);
  const limit = CLIENT_SIZE_LIMITS[type];
  if (file.size > limit) {
    return `${type} 上限 ${Math.round(limit / 1024 / 1024)} MB，本檔 ${formatBytes(file.size)}`;
  }
  return null;
}

/** 拖放 / 資料夾選取後的單一檔案，`path` 為含檔名的相對路徑（無目錄時為空字串）。 */
export interface PickedFile {
  file: File;
  path: string;
}

/** 明顯不該進媒體庫的系統檔；拖整個資料夾時一定會夾帶。 */
const JUNK_NAMES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);

function isJunk(name: string): boolean {
  return JUNK_NAMES.has(name) || name.startsWith('._');
}

/**
 * 由相對路徑取出目錄部分作為標籤。
 *
 * `assets/3/zh/1_str1.wav` → `assets/3/zh`。只給一個完整路徑標籤而非
 * `assets`、`assets/3`、`assets/3/zh` 三個累加前綴：樹狀導覽只要對 `/` 切分
 * 就能重建層級，存累加前綴只會讓 tags 欄位膨脹。
 */
export function folderTagOf(path: string): string | null {
  const dir = path.split('/').slice(0, -1).join('/');
  return dir === '' ? null : dir;
}

/** 窗屏資產的語系目錄名，與 MediaLibrary 的 LANGUAGES 一致（非 CMS 的 zh-TW/ja）。 */
const LANGUAGE_SEGMENTS = new Set(['zh', 'en', 'jp']);

/**
 * 由路徑推測語系（ITRI 素材樹是 `assets/{roi}/{lang}/…`）。
 *
 * 只在**整段目錄名完全等於** zh/en/jp 時才成立，避免把 `english-notes` 這種
 * 名稱誤判。推測結果會顯示在佇列列上，且批次語系選了值時以批次為準——不讓
 * 它變成看不見的魔法。
 */
export function languageFromPath(path: string): string | null {
  const segments = path.split('/').slice(0, -1);
  for (const s of segments) {
    if (LANGUAGE_SEGMENTS.has(s)) return s;
  }
  return null;
}

/**
 * 把使用者拖進來的路徑改寫成該景點的資料夾標籤。
 *
 * 「從資料夾套用」掃的是 `{roi}/{zh|en|jp}`（以及推薦點的
 * `{roi}/{lang}/{4,5}_{分類}`），但使用者手上那包資料夾叫什麼名字完全不由我們
 * 決定——可能是 `3`、可能是 `西子灣`、也可能他直接拖 `zh` / `en` / `jp` 三個
 * 資料夾進來。若照原路徑打標籤，只有剛好命名為 roi 編號的那一種會被掃到，其餘
 * 全部靜默落空：檔案進了媒體庫，計畫卻說「資料夾裡沒有東西」。
 *
 * 作法是**以語系段為錨點重寫前綴**——語系段之後的結構（推薦點子資料夾）原樣保留：
 *
 *   `3/zh/1_pic1.png`          + roi 5 → `5/zh`
 *   `西子灣/zh/5_ent/5_1.png`  + roi 5 → `5/zh/5_ent`
 *   `zh/1_voice1.wav`          + roi 5 → `5/zh`
 *
 * 找不到語系段回 null：那種檔案無從判斷要放哪一語系，呼叫端應該讓使用者知道它
 * 沒被處理，而不是猜一個語系塞進去——猜錯的下場是船上某個語系播到別的語言。
 */
export function retargetFolderTag(path: string, targetFolder: string): string | null {
  const dirs = path.split('/').slice(0, -1);
  const idx = dirs.findIndex((s) => LANGUAGE_SEGMENTS.has(s));
  if (idx < 0) return null;
  return [targetFolder, ...dirs.slice(idx)].join('/');
}

/**
 * `<input webkitdirectory>` 選出的檔案帶有 webkitRelativePath；一般選檔沒有。
 */
export function pickedFromFileList(files: FileList): PickedFile[] {
  return Array.from(files)
    .filter((f) => !isJunk(f.name))
    .map((f) => ({
      file: f,
      path: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name,
    }));
}

/**
 * 由拖放事件展開檔案，支援整個資料夾。
 *
 * 兩個容易踩到的點：
 *   1. `dataTransfer.items` 在事件處理函式回傳後即失效，故必須**同步**先把
 *      entry 取出來，之後才能 await 遞迴讀取。
 *   2. `readEntries` 每次最多回 100 筆，且不保證一次讀完 —— 必須反覆呼叫到
 *      回空陣列為止，否則大目錄會靜默少檔。
 */
export async function pickedFromDataTransfer(dt: DataTransfer): Promise<PickedFile[]> {
  const entries: FileSystemEntry[] = [];
  for (const item of Array.from(dt.items)) {
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry();
    if (entry) entries.push(entry);
  }

  // 瀏覽器不支援 webkitGetAsEntry（或非檔案拖放）時退回平面檔案清單
  if (entries.length === 0) return pickedFromFileList(dt.files);

  const out: PickedFile[] = [];
  for (const entry of entries) {
    await walkEntry(entry, '', out);
  }
  return out;
}

async function walkEntry(entry: FileSystemEntry, prefix: string, out: PickedFile[]): Promise<void> {
  if (out.length >= MAX_BATCH_FILES) return;

  if (entry.isFile) {
    const file = await new Promise<File | null>((resolve) => {
      (entry as FileSystemFileEntry).file(resolve, () => resolve(null));
    });
    if (file && !isJunk(file.name)) {
      out.push({ file, path: prefix ? `${prefix}/${file.name}` : file.name });
    }
    return;
  }

  if (!entry.isDirectory) return;
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  const nextPrefix = prefix ? `${prefix}/${entry.name}` : entry.name;

  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve) => {
      reader.readEntries(resolve, () => resolve([]));
    });
    if (batch.length === 0) break;
    for (const child of batch) {
      await walkEntry(child, nextPrefix, out);
      if (out.length >= MAX_BATCH_FILES) return;
    }
  }
}
